import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import {
    Brain, RefreshCw, Target, TrendingUp, Zap, AlertTriangle, Activity, BarChart2,
    Check, X, ChevronDown, ChevronUp, Crosshair, Flame, Award, Gauge
} from 'lucide-react';
import { toast } from 'sonner';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    ReferenceLine, BarChart, Bar, Cell, LineChart, Line, ComposedChart, ScatterChart, Scatter
} from 'recharts';

/* ═══ KEY OVER BADGE ═══ */
function KeyOverBadge({ type }) {
    const styles = {
        explosive: 'bg-red-500/20 text-red-400 border-red-500/30',
        big: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
        wicket_maiden: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
        maiden: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        multi_wicket: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
        wicket: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    };
    const labels = {
        explosive: 'EXPLOSIVE', big: 'BIG OVER', wicket_maiden: 'W-MAIDEN',
        maiden: 'MAIDEN', multi_wicket: 'MULTI-W', wicket: 'WICKET',
    };
    if (!type) return null;
    return (
        <span className={`text-[8px] px-1.5 py-0.5 rounded border tech-mono ${styles[type] || 'bg-white/10 text-white/40'}`}>
            {labels[type] || type}
        </span>
    );
}

/* ═══ MAIN PAGE ═══ */
export default function ModelRetrainingDashboard() {
    const [liveState, setLiveState] = useState(null);
    const [retrainDash, setRetrainDash] = useState(null);
    const [accuracyTrend, setAccuracyTrend] = useState(null);
    const [overAccuracy, setOverAccuracy] = useState(null);
    const [sentimentEvents, setSentimentEvents] = useState(null);
    const [deepStatus, setDeepStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [retraining, setRetraining] = useState(false);
    const [deepTraining, setDeepTraining] = useState(false);
    const [expandedPhase, setExpandedPhase] = useState(null);
    const [liveMatchId, setLiveMatchId] = useState(null);

    const fetchAll = useCallback(async () => {
        try {
            const [statusRes, dashRes, trendRes, overRes, deepRes] = await Promise.all([
                API.get('/live/realtime-status'),
                API.get('/intelligence/retrain/dashboard'),
                API.get('/intelligence/retrain/accuracy-trend'),
                API.get('/live/over-accuracy'),
                API.get('/training/status').catch(() => ({ data: null })),
            ]);
            setOverAccuracy(overRes.data);
            setRetrainDash(dashRes.data);
            setAccuracyTrend(trendRes.data);
            setDeepStatus(deepRes.data);

            const matches = statusRes.data?.matches || [];
            if (matches.length > 0) {
                const mid = matches[0].match_id;
                setLiveMatchId(mid);
                const [modelRes, sentRes] = await Promise.all([
                    API.get(`/live/model-state/${mid}`),
                    API.get(`/live/sentiment-events/${mid}`).catch(() => ({ data: null })),
                ]);
                setLiveState(modelRes.data);
                setSentimentEvents(sentRes.data);
            }
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchAll();
        const iv = setInterval(fetchAll, 20000);
        return () => clearInterval(iv);
    }, [fetchAll]);

    const triggerBatchRetrain = async () => {
        setRetraining(true);
        try {
            const res = await API.post('/intelligence/retrain/batch');
            toast.success(`Batch retrain complete: ${res.data?.total_retrained || 0} matches`);
            fetchAll();
        } catch (e) {
            toast.error('Retrain failed');
        }
        setRetraining(false);
    };

    const triggerDeepTraining = async () => {
        setDeepTraining(true);
        try {
            const res = await API.post('/training/deep-train');
            toast.success(`Deep training: ${res.data?.engines_trained || 0} engines trained in ${res.data?.elapsed_seconds || 0}s`);
            fetchAll();
        } catch (e) {
            toast.error('Deep training failed');
        }
        setDeepTraining(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
                <div className="w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const adjustments = liveState?.adjustments || [];
    const keyOvers = liveState?.key_overs || [];
    const milestones = liveState?.milestones || [];
    const phases = liveState?.momentum_phases || [];
    const fixture = liveState?.fixture || {};
    const homeTeam = fixture.home_team || {};
    const awayTeam = fixture.away_team || {};
    const haColor = homeTeam.color || '#00E5FF';
    const aaColor = awayTeam.color || '#FF2D95';
    const trendData = accuracyTrend?.trend || [];
    const sentEvents = sentimentEvents?.events || [];
    const sentTimeline = sentimentEvents?.timeline || [];

    return (
        <div className="space-y-5" data-testid="model-retraining-page">
            {/* Header */}
            <div className="glass-card p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Brain className="w-5 h-5 text-green-400" />
                        <h2 className="heading-primary text-base md:text-xl text-white">LIVE MODEL RETRAINING</h2>
                        {liveState?.overs_tracked > 0 && (
                            <div className="px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded-full tech-mono border border-green-500/30 animate-pulse">TRACKING</div>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={triggerDeepTraining} disabled={deepTraining} data-testid="deep-train-btn"
                            className="px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg text-xs hover:bg-amber-500/30 transition-colors disabled:opacity-50 flex items-center gap-2">
                            <Flame className={`w-3 h-3 ${deepTraining ? 'animate-spin' : ''}`} />
                            {deepTraining ? 'Deep Training...' : 'Deep Train All Engines'}
                        </button>
                        <button onClick={triggerBatchRetrain} disabled={retraining} data-testid="batch-retrain-btn"
                            className="px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/30 rounded-lg text-xs hover:bg-green-500/30 transition-colors disabled:opacity-50 flex items-center gap-2">
                            <RefreshCw className={`w-3 h-3 ${retraining ? 'animate-spin' : ''}`} />
                            {retraining ? 'Retraining...' : 'Batch Retrain'}
                        </button>
                        <div className="text-[10px] text-white/20 tech-mono">AUTO-REFRESH 20s</div>
                    </div>
                </div>
            </div>

            {/* Deep Training Status */}
            {deepStatus?.status && (
                <div className="glass-card p-5 border border-amber-500/20" data-testid="deep-training-status">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <Flame className="w-4 h-4 text-amber-400" />
                            <div>
                                <div className="text-xs font-bold text-white">AUTONOMOUS TRAINING ENGINE</div>
                                <div className="text-[10px] text-white/30 tech-mono">
                                    Runs after every match + daily at 3 AM IST | Last: {deepStatus.status.last_trained ? new Date(deepStatus.status.last_trained).toLocaleString() : 'Never'}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-center">
                                <div className="text-[10px] text-white/30 tech-mono">ACCURACY</div>
                                <div className="text-lg font-bold text-cyan-400">{deepStatus.status.current_accuracy || 0}%</div>
                            </div>
                            <div className="text-center">
                                <div className="text-[10px] text-white/30 tech-mono">TOTAL RUNS</div>
                                <div className="text-lg font-bold text-white">{deepStatus.total_runs || 0}</div>
                            </div>
                        </div>
                    </div>
                    {/* Engine Status Grid */}
                    {deepStatus.status.engines_status && (
                        <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mt-3">
                            {Object.entries(deepStatus.status.engines_status).map(([engine, status]) => (
                                <div key={engine} className={`text-center p-2 rounded-lg ${status === 'trained' ? 'bg-green-500/10 border border-green-500/20' : 'bg-white/[0.03] border border-white/5'}`}>
                                    <div className={`text-[10px] font-bold ${status === 'trained' ? 'text-green-400' : 'text-white/30'}`}>
                                        {status === 'trained' ? <Check className="w-3 h-3 mx-auto" /> : <X className="w-3 h-3 mx-auto text-white/20" />}
                                    </div>
                                    <div className="text-[8px] text-white/40 tech-mono mt-0.5">{engine.replace(/_/g, ' ').toUpperCase()}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Recent Training Runs */}
                    {deepStatus.recent_runs?.length > 0 && (
                        <div className="mt-3 space-y-1">
                            <div className="text-[10px] text-white/30 tech-mono">RECENT TRAINING RUNS</div>
                            {deepStatus.recent_runs.slice(0, 3).map((run, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-white/[0.02] rounded-lg text-xs">
                                    <span className="text-white/60">{run.trigger}</span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10px] text-white/30 tech-mono">{run.engines_trained} engines</span>
                                        <span className="text-[10px] text-white/20">{run.elapsed_seconds}s</span>
                                        <span className="text-[10px] text-white/20">{run.completed_at ? new Date(run.completed_at).toLocaleString() : ''}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Live Match Status */}
            {liveMatchId && (
                <div className="glass-card p-5 border-l-4 border-l-green-500" data-testid="live-match-model">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-xs text-white/40 tech-mono">LIVE MODEL STATE</div>
                            <div className="text-lg font-bold text-white">{homeTeam.short_name} vs {awayTeam.short_name}</div>
                            <div className="text-[10px] text-white/20">{fixture.venue?.name} | {fixture.status}</div>
                        </div>
                        <div className="flex gap-6">
                            <div className="text-center">
                                <div className="text-[10px] text-white/30 tech-mono">OVERS TRACKED</div>
                                <div className="heading-primary text-3xl text-green-400">{liveState?.overs_tracked || 0}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-[10px] text-white/30 tech-mono">AVG DEVIATION</div>
                                <div className="heading-primary text-3xl text-yellow-400">{liveState?.avg_deviation?.toFixed(1) || '0'}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-[10px] text-white/30 tech-mono">WIN ADJ</div>
                                <div className={`heading-primary text-3xl ${(liveState?.total_adjustment || 0) >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                                    {liveState?.total_adjustment >= 0 ? '+' : ''}{liveState?.total_adjustment?.toFixed(1) || '0'}%
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-[10px] text-white/30 tech-mono">KEY OVERS</div>
                                <div className="heading-primary text-3xl text-purple-400">{keyOvers.length}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Over-by-Over Deviation Chart */}
            {adjustments.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-8 glass-card p-5" data-testid="deviation-chart">
                        <div className="flex items-center gap-2 mb-3">
                            <BarChart2 className="w-4 h-4 text-cyan-400" />
                            <h3 className="heading-primary text-sm text-white">EXPECTED vs ACTUAL RUN RATE</h3>
                            <div className="flex items-center gap-2 ml-auto">
                                <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded bg-cyan-400" /><span className="text-[9px] text-white/30">Actual</span></div>
                                <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded bg-white/20" /><span className="text-[9px] text-white/30">Expected</span></div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart data={adjustments}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                                <XAxis dataKey="over" tick={{ fill: '#555', fontSize: 10 }} />
                                <YAxis tick={{ fill: '#555', fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, fontSize: 11 }} />
                                <ReferenceLine y={8} stroke="#333" strokeDasharray="5 5" label={{ value: 'Par', fill: '#444', fontSize: 9 }} />
                                <Bar dataKey="actual_rr" name="Actual RR" radius={[4, 4, 0, 0]}>
                                    {adjustments.map((d, i) => (
                                        <Cell key={i} fill={d.actual_rr >= d.expected_rr ? '#00E5FF' : '#FF4444'} fillOpacity={0.6} />
                                    ))}
                                </Bar>
                                <Line type="monotone" dataKey="expected_rr" name="Expected RR" stroke="#555" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Win Adjustment Timeline */}
                    <div className="lg:col-span-4 glass-card p-5" data-testid="win-adjustment-chart">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="w-4 h-4 text-green-400" />
                            <h3 className="heading-primary text-sm text-white">CUMULATIVE ADJUSTMENT</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={adjustments.map((d, i) => ({
                                ...d,
                                cumAdj: adjustments.slice(0, i + 1).reduce((s, x) => s + (x.adjustment || 0), 0),
                            }))}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                                <XAxis dataKey="over" tick={{ fill: '#555', fontSize: 10 }} />
                                <YAxis tick={{ fill: '#555', fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, fontSize: 11 }} />
                                <ReferenceLine y={0} stroke="#444" />
                                <defs>
                                    <linearGradient id="adjGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#39FF14" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#39FF14" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <Area type="monotone" dataKey="cumAdj" stroke="#39FF14" fill="url(#adjGrad)" strokeWidth={2} name="Cum. Adjustment %" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Key Overs + Milestones + Momentum Phases */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Key Overs */}
                <div className="glass-card p-5" data-testid="key-overs">
                    <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4 text-yellow-400" />
                        <h3 className="heading-primary text-sm text-white">KEY OVERS</h3>
                    </div>
                    {keyOvers.length > 0 ? (
                        <div className="space-y-2 max-h-[280px] overflow-y-auto">
                            {keyOvers.map((k, i) => (
                                <div key={i} className="flex items-center justify-between p-2.5 bg-white/5 rounded-lg border border-white/5">
                                    <div className="flex items-center gap-2">
                                        <div className="text-lg font-bold text-white/30 tech-mono w-8">#{k.over}</div>
                                        <div>
                                            <div className="text-xs text-white">{k.reason}</div>
                                            <KeyOverBadge type={k.type} />
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-bold text-white">{k.runs}</div>
                                        <div className="text-[9px] text-white/30">runs</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-white/20 text-xs py-8">No key overs yet</div>
                    )}
                </div>

                {/* Milestones */}
                <div className="glass-card p-5" data-testid="milestones">
                    <div className="flex items-center gap-2 mb-3">
                        <Award className="w-4 h-4 text-amber-400" />
                        <h3 className="heading-primary text-sm text-white">MILESTONES</h3>
                    </div>
                    {milestones.length > 0 ? (
                        <div className="space-y-2">
                            {milestones.map((m, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-amber-500/5 rounded-lg border border-amber-500/10">
                                    <div className="flex items-center gap-2">
                                        <Target className="w-4 h-4 text-amber-400" />
                                        <span className="text-sm text-amber-400 font-bold">{m.milestone?.replace('_', ' ').toUpperCase()}</span>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-white/40">Over {m.over}</div>
                                        <div className="text-sm text-white font-bold">{m.score}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-white/20 text-xs py-8">No milestones yet</div>
                    )}
                </div>

                {/* Momentum Phases */}
                <div className="glass-card p-5" data-testid="momentum-phases">
                    <div className="flex items-center gap-2 mb-3">
                        <Flame className="w-4 h-4 text-orange-400" />
                        <h3 className="heading-primary text-sm text-white">MOMENTUM PHASES</h3>
                    </div>
                    {phases.length > 0 ? (
                        <div className="space-y-2 max-h-[280px] overflow-y-auto">
                            {phases.map((p, i) => {
                                const phColors = { attacking: 'text-green-400 bg-green-500/10 border-green-500/20', defensive: 'text-red-400 bg-red-500/10 border-red-500/20', normal: 'text-white/50 bg-white/5 border-white/10' };
                                return (
                                    <div key={i} className={`p-3 rounded-lg border cursor-pointer ${phColors[p.type] || phColors.normal}`}
                                        onClick={() => setExpandedPhase(expandedPhase === i ? null : i)}>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-bold uppercase">{p.type}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] tech-mono">OV {p.start}-{p.end}</span>
                                                {expandedPhase === i ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                            </div>
                                        </div>
                                        {expandedPhase === i && (
                                            <div className="mt-2 pt-2 border-t border-white/10 grid grid-cols-3 gap-2 text-center">
                                                <div><div className="text-lg font-bold">{p.overs}</div><div className="text-[9px] text-white/30">OVERS</div></div>
                                                <div><div className="text-lg font-bold">{p.runs}</div><div className="text-[9px] text-white/30">RUNS</div></div>
                                                <div><div className="text-lg font-bold">{p.rr}</div><div className="text-[9px] text-white/30">RR</div></div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center text-white/20 text-xs py-8">Calculating phases...</div>
                    )}
                </div>
            </div>

            {/* Sentiment-Event Correlation */}
            {sentEvents.length > 0 && (
                <div className="glass-card p-5" data-testid="sentiment-event-chart">
                    <div className="flex items-center gap-2 mb-3">
                        <Activity className="w-4 h-4 text-pink-400" />
                        <h3 className="heading-primary text-sm text-white">SENTIMENT x MATCH EVENTS</h3>
                        <span className="text-[9px] px-2 py-0.5 bg-pink-500/20 text-pink-400 rounded tech-mono">REAL-TIME</span>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={sentTimeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                            <XAxis dataKey="over" tick={{ fill: '#555', fontSize: 10 }} />
                            <YAxis yAxisId="left" domain={[0, 10]} tick={{ fill: '#555', fontSize: 10 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fill: '#555', fontSize: 10 }} />
                            <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, fontSize: 11 }} />
                            <Line yAxisId="left" type="monotone" dataKey="team_a_sentiment" stroke={haColor} strokeWidth={2} name={`${homeTeam.short_name} Sentiment`} dot={false} connectNulls />
                            <Line yAxisId="left" type="monotone" dataKey="team_b_sentiment" stroke={aaColor} strokeWidth={2} name={`${awayTeam.short_name} Sentiment`} dot={false} connectNulls />
                            <Bar yAxisId="right" dataKey="runs" name="Runs" fill="#ffffff10" radius={[2, 2, 0, 0]}>
                                {sentTimeline.map((d, i) => (
                                    <Cell key={i} fill={d.key_over_type ? '#FF4444' : '#ffffff10'} />
                                ))}
                            </Bar>
                        </ComposedChart>
                    </ResponsiveContainer>
                    <div className="flex gap-3 mt-2 justify-center">
                        {sentEvents.map((e, i) => (
                            <div key={i} className="text-[9px] text-white/30">
                                <span className="text-white/50">OV{e.over}:</span> {e.event_type}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Elo Ratings + Impact Adjustments */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Elo Ratings */}
                {liveState?.elo_ratings && Object.keys(liveState.elo_ratings).length > 0 && (
                    <div className="glass-card p-5" data-testid="elo-ratings">
                        <div className="flex items-center gap-2 mb-3">
                            <Gauge className="w-4 h-4 text-cyan-400" />
                            <h3 className="heading-primary text-sm text-white">ELO RATINGS</h3>
                        </div>
                        <div className="space-y-3">
                            {Object.entries(liveState.elo_ratings).map(([tid, rating]) => {
                                const team = tid === homeTeam.id ? homeTeam : awayTeam;
                                const color = tid === homeTeam.id ? haColor : aaColor;
                                return (
                                    <div key={tid} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                                            <span className="text-sm text-white">{team.short_name || tid}</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xl font-bold" style={{ color }}>{Math.round(rating)}</div>
                                            <div className="text-[9px] text-white/30">Elo Rating</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Impact Adjustments */}
                {(liveState?.impact_adjustments || []).length > 0 && (
                    <div className="glass-card p-5" data-testid="impact-adjustments">
                        <div className="flex items-center gap-2 mb-3">
                            <Crosshair className="w-4 h-4 text-purple-400" />
                            <h3 className="heading-primary text-sm text-white">IMPACT PLAYER ADJUSTMENTS</h3>
                        </div>
                        <div className="space-y-2">
                            {liveState.impact_adjustments.map((a, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-purple-500/5 rounded-lg border border-purple-500/10">
                                    <div>
                                        <div className="text-sm text-white font-bold">{a.player}</div>
                                        <div className="text-[10px] text-white/30">{a.team}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`text-sm font-bold ${a.net_shift >= 0 ? 'text-green-400' : 'text-red-400'}`}>{a.net_shift >= 0 ? '+' : ''}{a.net_shift?.toFixed(1)}%</div>
                                        <div className="text-[9px] text-white/30">{a.original_pct}% → {a.adjusted_pct}%</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Accuracy Trend + Overall Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-8 glass-card p-5" data-testid="accuracy-trend-chart">
                    <div className="flex items-center gap-2 mb-3">
                        <Target className="w-4 h-4 text-green-400" />
                        <h3 className="heading-primary text-sm text-white">MODEL ACCURACY TREND</h3>
                    </div>
                    {trendData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={trendData.map((d, i) => ({ ...d, index: i + 1 }))}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                                <XAxis dataKey="index" tick={{ fill: '#555', fontSize: 10 }} label={{ value: 'Match #', position: 'bottom', fill: '#444', fontSize: 9 }} />
                                <YAxis domain={[0, 100]} tick={{ fill: '#555', fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, fontSize: 11 }} />
                                <ReferenceLine y={50} stroke="#333" strokeDasharray="5 5" />
                                <Line type="monotone" dataKey="accuracy" stroke="#39FF14" strokeWidth={2} dot={{ fill: '#39FF14', r: 3 }} name="Accuracy %" />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[220px] text-white/20 text-sm">Accuracy trend populates after matches complete</div>
                    )}
                </div>

                {/* Over-by-Over Accuracy */}
                <div className="lg:col-span-4 glass-card p-5" data-testid="over-accuracy-stats">
                    <div className="flex items-center gap-2 mb-4">
                        <Gauge className="w-4 h-4 text-amber-400" />
                        <h3 className="heading-primary text-sm text-white">BALL-BY-BALL STATS</h3>
                    </div>
                    <div className="space-y-4">
                        <div className="text-center p-4 bg-white/5 rounded-lg">
                            <div className="heading-primary text-4xl text-green-400">{overAccuracy?.total_overs_tracked || 0}</div>
                            <div className="text-xs text-white/30 mt-1">Overs Tracked</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="text-center p-3 bg-white/5 rounded-lg">
                                <div className="text-xl font-bold text-yellow-400">{overAccuracy?.avg_deviation?.toFixed(1) || '0'}</div>
                                <div className="text-[9px] text-white/30">Avg Deviation</div>
                            </div>
                            <div className="text-center p-3 bg-white/5 rounded-lg">
                                <div className="text-xl font-bold text-cyan-400">{overAccuracy?.matches_with_data || 0}</div>
                                <div className="text-[9px] text-white/30">Matches</div>
                            </div>
                        </div>
                        <div className="text-center p-3 bg-white/5 rounded-lg">
                            <div className="text-xl font-bold text-white">{accuracyTrend?.current_accuracy?.toFixed(1) || '0'}%</div>
                            <div className="text-[9px] text-white/30">Overall Model Accuracy</div>
                        </div>
                        <div className="text-center p-3 bg-white/5 rounded-lg">
                            <div className="text-xl font-bold text-white">{accuracyTrend?.total_matches_analyzed || 0}</div>
                            <div className="text-[9px] text-white/30">Matches Analyzed</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Prediction Analysis */}
            {liveState?.prediction_analysis && (
                <div className="glass-card p-5" data-testid="prediction-analysis">
                    <div className="flex items-center gap-2 mb-3">
                        {liveState.prediction_analysis.correct ? <Check className="w-4 h-4 text-green-400" /> : <X className="w-4 h-4 text-red-400" />}
                        <h3 className="heading-primary text-sm text-white">PREDICTION ANALYSIS</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-white/5 rounded-lg text-center">
                            <div className="text-xs text-white/40">Predicted</div>
                            <div className="text-sm text-white font-bold">{liveState.prediction_analysis.predicted_winner}</div>
                        </div>
                        <div className="p-3 bg-white/5 rounded-lg text-center">
                            <div className="text-xs text-white/40">Actual</div>
                            <div className="text-sm text-white font-bold">{liveState.prediction_analysis.actual_winner}</div>
                        </div>
                        <div className="p-3 bg-white/5 rounded-lg text-center">
                            <div className="text-xs text-white/40">Result</div>
                            <div className={`text-sm font-bold ${liveState.prediction_analysis.correct ? 'text-green-400' : 'text-red-400'}`}>
                                {liveState.prediction_analysis.correct ? 'CORRECT' : 'WRONG'}
                            </div>
                        </div>
                        <div className="p-3 bg-white/5 rounded-lg text-center">
                            <div className="text-xs text-white/40">Error</div>
                            <div className="text-sm text-white font-bold">{liveState.prediction_analysis.prediction_error?.toFixed(1)}%</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Retrain Dashboard Weight Suggestions */}
            {retrainDash?.weight_suggestions?.suggestions?.length > 0 && (
                <div className="glass-card p-5" data-testid="weight-suggestions">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <h3 className="heading-primary text-sm text-white">WEIGHT ADJUSTMENT SUGGESTIONS</h3>
                    </div>
                    <div className="space-y-2">
                        {retrainDash.weight_suggestions.suggestions.map((s, i) => (
                            <div key={i} className="p-3 bg-amber-500/5 rounded-lg border border-amber-500/10 text-xs text-amber-400/80">{s}</div>
                        ))}
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="text-center text-[10px] text-white/10 tech-mono">
                LIVE MODEL RETRAINING — V-ONE AI v3.0 — {liveState?.overs_tracked || 0} OVERS | {keyOvers.length} KEY EVENTS | {sentEvents.length} SENTIMENT SNAPSHOTS
            </div>
        </div>
    );
}
